"use client";

import { useEffect, useState } from "react";
import { TweetSkeleton } from "react-tweet";
import { CustomEmbeddedTweetClient } from "./custom-embedded-tweet-client";
import {
  type DeferredTweetThreadItem,
  fetchDeferredTweetThread,
} from "./deferred-tweet-thread.helpers";

type DeferredTweetThreadProps = {
  tweetIds: string[];
  className?: string;
};

/**
 * Client-side tweet thread that batches tweet fetches in API-sized chunks.
 * Avoids N+1 requests that would occur with individual DeferredTweetEmbed per tweet.
 */
export function DeferredTweetThread({
  tweetIds,
  className,
}: DeferredTweetThreadProps) {
  const [tweets, setTweets] = useState<DeferredTweetThreadItem[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function fetchTweets() {
      try {
        const threadTweets = await fetchDeferredTweetThread({
          tweetIds,
          fetchBatch: async ({ url }) => fetch(url),
        });

        if (threadTweets === null || cancelled) {
          return;
        }

        setTweets(threadTweets);
      } finally {
        if (!cancelled) {
          setLoaded(true);
        }
      }
    }

    fetchTweets();
    return () => {
      cancelled = true;
    };
  }, [tweetIds]);

  if (!loaded) {
    return (
      <div className={className}>
        {tweetIds.map((id) => (
          <div className="[&_.react-tweet-theme]:!bg-transparent" key={id}>
            <TweetSkeleton />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={className}>
      {tweets.map((item) => {
        if (!item.tweet) {
          return null;
        }
        return (
          <div
            className="[&_.react-tweet-theme]:!bg-transparent"
            key={item.tweetId}
          >
            <CustomEmbeddedTweetClient tweet={item.tweet} />
          </div>
        );
      })}
    </div>
  );
}
